// File: /src/hooks/finance/useFinanceEntries.js

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { applySummaryDelta } from './useFinanceSummary';
import { buildLineSeriesByPeriod } from '../../utils/financeMetrics';

export const DEFAULT_FORM_STATE = {
	description: '',
	value: '',
	type: 'income',
	date: '',
};

const toISODate = (value) => {
	if (!value) {
		return value;
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const sortByDateAsc = (a, b) => {
	const left = new Date(a.date);
	const right = new Date(b.date);
	return left.getTime() - right.getTime();
};

export const useFinanceEntries = ({
	manualEntries = [],
	paidInvoices = [],
	allInvoices = [],
	setManualEntries,
	setFinanceSummary,
	refreshFinanceData,
	showConfirm,
} = {}) => {
	const [showModal, setShowModal] = useState(false);
	const [editingEntry, setEditingEntry] = useState(null);
	const [form, setForm] = useState(DEFAULT_FORM_STATE);
	const [period, setPeriod] = useState('monthly');

	const activeManualEntries = useMemo(
		() => manualEntries.filter((entry) => !entry?.deleted),
		[manualEntries],
	);

	const invoiceEntries = useMemo(
		() =>
			paidInvoices.map((invoice) => ({
				id: `inv-${invoice.id}`,
				description: invoice.description,
				date: invoice.due_date,
				value: invoice.value,
				type: 'invoice',
			})),
		[paidInvoices],
	);

	const overdueInvoices = useMemo(
		() =>
			allInvoices
				.filter((invoice) => invoice.status === 'overdue')
				.map((invoice) => ({
					id: `ovd-${invoice.id}`,
					description: invoice.description,
					date: invoice.due_date,
					value: invoice.value,
					type: 'overdue',
				})),
		[allInvoices],
	);

	const tableEntries = useMemo(() => {
		const combined = [
			...activeManualEntries,
			...invoiceEntries,
			...overdueInvoices,
		];

		return combined.sort(sortByDateAsc);
	}, [activeManualEntries, invoiceEntries, overdueInvoices]);

	const chartSourceEntries = useMemo(() => {
		const manualForChart = manualEntries.map((entry) => ({
			...entry,
			type: entry.type,
		}));

		const paidForChart = paidInvoices.map((invoice) => ({
			id: invoice.id,
			description: invoice.description,
			date: invoice.due_date,
			value: invoice.value,
			type: 'paid',
		}));

		const pendingForChart = allInvoices
			.filter((invoice) => invoice.status === 'pending')
			.map((invoice) => ({
				id: invoice.id,
				description: invoice.description,
				date: invoice.due_date,
				value: invoice.value,
				type: 'pending',
			}));

		const overdueForChart = allInvoices
			.filter((invoice) => invoice.status === 'overdue')
			.map((invoice) => ({
				id: invoice.id,
				description: invoice.description,
				date: invoice.due_date,
				value: invoice.value,
				type: 'overdue',
			}));

		return [
			...manualForChart,
			...paidForChart,
			...pendingForChart,
			...overdueForChart,
		];
	}, [manualEntries, paidInvoices, allInvoices]);

	const lineData = useMemo(
		() => buildLineSeriesByPeriod(chartSourceEntries, period),
		[chartSourceEntries, period],
	);

	const resetForm = useCallback(() => {
		setForm(DEFAULT_FORM_STATE);
		setEditingEntry(null);
	}, []);

	const openModalForCreation = useCallback(() => {
		resetForm();
		setShowModal(true);
	}, [resetForm]);

	const startEditingEntry = useCallback((entry) => {
		if (!entry) return;

		setEditingEntry(entry);
		setForm({
			description: entry.description ?? '',
			value: entry.value ?? '',
			type: entry.type ?? 'income',
			date: entry.date ? new Date(entry.date) : '',
		});
		setShowModal(true);
	}, []);

	const closeModal = useCallback(() => {
		setShowModal(false);
		resetForm();
	}, [resetForm]);

	const handleInputChange = useCallback((event) => {
		const { name, value } = event.target;
		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	}, []);

	const applySummaryAdjustment = useCallback(
		(incomeDelta, expenseDelta) => {
			if (!setFinanceSummary) return;
			setFinanceSummary((prev) =>
				applySummaryDelta(prev, { incomeDelta, expenseDelta }),
			);
		},
		[setFinanceSummary],
	);

	const refreshDataSafely = useCallback(async () => {
		if (!refreshFinanceData) return;
		try {
			await refreshFinanceData(true);
		} catch (err) {
			// errors already surfaced via toast/interceptor
		}
	}, [refreshFinanceData]);

	const addOrUpdateFinanceEntry = useCallback(
		async (event) => {
			event.preventDefault();

			if (!form.description || !form.value || !form.date) {
				toast.error('Preencha todos os campos!');
				return;
			}

			if (editingEntry && editingEntry.type === 'invoice') {
				toast.error('Não é possível editar faturas pagas aqui.');
				return;
			}

			try {
				const numericValue = parseFloat(form.value);
				const payload = {
					description: form.description,
					value: numericValue,
					type: form.type,
					date: toISODate(form.date),
				};

				if (editingEntry) {
					const response = await apiClient.put(
						API_ENDPOINTS.financeEntryById(editingEntry.id),
						payload,
					);

					setManualEntries((prev = []) =>
						prev.map((entry) =>
							entry.id === editingEntry.id
								? response.data
								: entry,
						),
					);

					const oldValue = parseFloat(editingEntry.value) || 0;
					const incomeDelta =
						(payload.type === 'income' ? numericValue : 0) -
						(editingEntry.type === 'income' ? oldValue : 0);
					const expenseDelta =
						(payload.type === 'expense' ? numericValue : 0) -
						(editingEntry.type === 'expense' ? oldValue : 0);

					applySummaryAdjustment(incomeDelta, expenseDelta);
					toast.success('Entrada atualizada com sucesso!');
				} else {
					const response = await apiClient.post(
						API_ENDPOINTS.FINANCE_ENTRY,
						payload,
					);

					setManualEntries((prev = []) => [...prev, response.data]);

					const incomeDelta =
						payload.type === 'income' ? numericValue : 0;
					const expenseDelta =
						payload.type === 'expense' ? numericValue : 0;

					applySummaryAdjustment(incomeDelta, expenseDelta);
					toast.success('Entrada adicionada com sucesso!');
				}

				setShowModal(false);
				resetForm();
			} catch (error) {
				console.error('Erro ao salvar entrada financeira:', error);
				toast.error('Erro ao salvar entrada financeira.');
			} finally {
				await refreshDataSafely();
			}
		},
		[
			form.description,
			form.value,
			form.date,
			form.type,
			editingEntry,
			setManualEntries,
			applySummaryAdjustment,
			resetForm,
			refreshDataSafely,
		],
	);

	const deleteEntry = useCallback(
		async (entry) => {
			if (!entry) return;

			if (entry.type === 'invoice') {
				toast.error('Não é possível excluir faturas pagas aqui.');
				return;
			}

			if (showConfirm) {
				const confirmed = await showConfirm({
					title: 'Excluir Entrada',
					message: 'Tem certeza que deseja excluir esta entrada? Esta ação não pode ser desfeita.',
					confirmText: 'Excluir',
					cancelText: 'Cancelar',
					variant: 'danger',
				});

				if (!confirmed) {
					return;
				}
			}

			try {
				await apiClient.delete(
					API_ENDPOINTS.financeEntryById(entry.id),
				);

				setManualEntries((prev = []) =>
					prev.filter((item) => item.id !== entry.id),
				);

				const numericValue = parseFloat(entry.value) || 0;
				const incomeDelta = entry.type === 'income' ? -numericValue : 0;
				const expenseDelta =
					entry.type === 'expense' ? -numericValue : 0;

				applySummaryAdjustment(incomeDelta, expenseDelta);
				toast.success('Entrada excluída com sucesso!');
			} catch (error) {
				console.error('Erro ao excluir entrada financeira:', error);
				toast.error('Erro ao excluir entrada financeira.');
			} finally {
				await refreshDataSafely();
			}
		},
		[applySummaryAdjustment, refreshDataSafely, setManualEntries, showConfirm],
	);

	return {
		tableEntries,
		lineData,
		period,
		setPeriod,
		showModal,
		openModalForCreation,
		startEditingEntry,
		closeModal,
		handleInputChange,
		addOrUpdateFinanceEntry,
		deleteEntry,
		form,
		editingEntry,
	};
};

export default useFinanceEntries;
